import { Link } from 'react-router-dom';
import { ExternalLink } from '@/utils/icons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoTooltip } from '@/components/common/InfoTooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/common/StatusBadge';
import { formatDateTime, formatDuration } from '@/utils/formatters';
import type { StateEvent } from '@/types/node';

interface StateChangesTableProps {
  events: StateEvent[];
}

export function StateChangesTable({ events }: StateChangesTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="dls-flex dls-items-center dls-gap-1.5">
          Recent State Changes
          <InfoTooltip text="Recent transitions between ALIVE and UNREACHABLE states. Duration shows how long the Node was in its previous state." />
        </CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <p className="dls-text-sm dls-text-muted-foreground">No state changes in this period</p>
        ) : (
          <div className="dls-max-h-[400px] dls-overflow-y-auto dls-overflow-x-auto">
            <Table className="dls-min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Node Name</TableHead>
                  <TableHead>S/N</TableHead>
                  <TableHead>Hardware</TableHead>
                  <TableHead>Previous</TableHead>
                  <TableHead>New</TableHead>
                  <TableHead>Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell className="dls-text-sm">{formatDateTime(event.changedAt)}</TableCell>
                    <TableCell className="dls-text-sm dls-font-medium">
                      {event.nodeId ? (
                        <Link
                          to={`/nodes/${event.nodeId}`}
                          className="hover:dls-underline dls-flex dls-items-center dls-gap-1"
                        >
                          {event.nodeName || 'Unknown Node'}
                          <ExternalLink className="dls-h-3 dls-w-3 dls-text-muted-foreground" />
                        </Link>
                      ) : (
                        event.nodeName || 'Unknown Node'
                      )}
                    </TableCell>
                    <TableCell className="dls-text-sm dls-text-muted-foreground">
                      {event.nodeSerialNumber || '—'}
                    </TableCell>
                    <TableCell className="dls-text-sm dls-text-muted-foreground dls-truncate dls-max-w-[150px]">
                      {event.nodeHardwareType || '—'}
                    </TableCell>
                    <TableCell>
                      {event.previousState ? (
                        <StatusBadge state={event.previousState} />
                      ) : (
                        <span className="dls-text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge state={event.newState} />
                    </TableCell>
                    <TableCell className="dls-text-sm dls-text-muted-foreground">
                      {formatDuration(event.durationSeconds)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
